import { FetchResult } from '@apollo/client';

import { MetadataError } from '@ep/error-handling';
import type { Metric } from '@esg/esg-global-types';
import {
  BaseMutationOptions,
  CreateMetricClientData,
  CreateMetricsOptions,
  CreateMetricsRequest,
  ResolverMutationRequestMetadata,
  UpdateMetricClientData,
  UpdateMetricRequest,
  parseResolverResponse
} from '@esg/syntiro-api-core';

import { validateParams } from '../validators/base.validator';
import { isValidCompanyId } from '../validators/company.validator';
import { isValidGroupId } from '../validators/group.validator';
import {
  CREATE_METRICS,
  SYNC_MASTER_LIST_METRICS,
  UPDATE_METRIC
} from '../mutations/metric.mutations';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { apollo_client_v2 } from '../../App';

const SOURCE = 'metric.service.ts';

/**
 * Result of a `syncMasterListMetrics` mutation. Local result shape mirroring the v2 schema's
 * `SyncMasterListMetricsResult` type — this is not part of `@esg/syntiro-api-core` (only the
 * request type lives there) and has no upstream equivalent.
 */
export interface SyncMasterListMetricsResult {
  updated: number;
  skipped_invalid_scope: number;
}

/**
 * Repairs (updates) company-level metric documents whose fields have drifted from their
 * master-list counterpart. When both `group_id` and `company_id` are supplied on `metadata`, only
 * that company is synced; when neither is, every company across every group is synced (global
 * mode). Supplying exactly one of the two throws (enforced server-side, see the v2 service).
 * @param {ResolverMutationRequestMetadata} metadata Metadata for the request. `group_id`/`company_id`
 * are optional here — see above. `audit_user_email` is set by this function from the current
 * Firebase user, matching every other v2 frontend service; callers should not pass it.
 * @param {BaseMutationOptions} options Optional mutation options (e.g. `create_audit_log`).
 * @returns {Promise<SyncMasterListMetricsResult | null | undefined>}
 */
export const syncMasterListMetrics = async (
  metadata: ResolverMutationRequestMetadata,
  options?: BaseMutationOptions
): Promise<SyncMasterListMetricsResult | null | undefined> => {
  validateParams(
    { metadata },
    {
      // Optional-tolerant: bare isValidGroupId/isValidCompanyId reject `undefined`, which global
      // mode requires. Mirrors company.service.ts:55's wrapper pattern.
      'metadata.group_id': {
        validate: (group_id: unknown) => !group_id || isValidGroupId(group_id),
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: (company_id: unknown) => !company_id || isValidCompanyId(company_id),
        message: 'Invalid company_id specified.'
      }
    }
  );

  const input: { metadata: ResolverMutationRequestMetadata; options?: BaseMutationOptions } = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    options
  };

  return apollo_client_v2
    .mutate({ mutation: SYNC_MASTER_LIST_METRICS, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.syncMasterListMetrics) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('syncMasterListMetrics'));
      }
      // parseResolverResponse, not `.result` raw: it throws on `metadata.errors` being non-empty,
      // which is how a v2 ERROR envelope (HTTP 200, result: null) surfaces as a failure here
      // instead of silently reporting success.
      return parseResolverResponse<SyncMasterListMetricsResult>(
        response.data.syncMasterListMetrics
      );
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'syncMasterListMetrics'),
        { metadata, options },
        trackingId
      );
    });
};

/**
 * Updates a single Metric document — company-level or master-list — through the v2 API. This is
 * the GE-2907 fix on the edit path: the server refuses (rather than silently nulling) a `scope`
 * object that carries no `.reference`.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for the request. On the master-list
 * screen, callers must send `{ master_list: true }` with `group_id`/`company_id` omitted; on the
 * company screen, `{ master_list: false, group_id, company_id }`. `audit_user_email` is set by
 * this function from the current Firebase user, matching every other v2 frontend service; callers
 * should not pass it.
 * @param {UpdateMetricClientData} data The patch to apply, including `original_metric` for the
 * audit log.
 * @param {BaseMutationOptions} options Optional mutation options (e.g. `create_audit_log`).
 * @returns {Promise<Metric | null | undefined>}
 */
export const updateMetric = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateMetricClientData,
  options?: BaseMutationOptions
): Promise<Metric | null | undefined> => {
  validateParams(
    { metadata },
    {
      // Optional-tolerant: bare isValidGroupId/isValidCompanyId reject `undefined`, which the
      // master-list screen requires (group_id/company_id omitted entirely).
      'metadata.group_id': {
        validate: (group_id: unknown) => !group_id || isValidGroupId(group_id),
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: (company_id: unknown) => !company_id || isValidCompanyId(company_id),
        message: 'Invalid company_id specified.'
      }
    }
  );

  const input: UpdateMetricRequest['input'] = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    data,
    options
  };

  return apollo_client_v2
    .mutate({ mutation: UPDATE_METRIC, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.updateMetric) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateMetric'));
      }
      // parseResolverResponse, not `.result` raw: it throws on `metadata.errors` being non-empty,
      // which is how a v2 ERROR envelope (HTTP 200, result: null) surfaces as a failure here
      // instead of silently reporting success.
      return parseResolverResponse<Metric>(response.data.updateMetric);
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error ? error.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateMetric'),
        { metadata, data, options },
        trackingId
      );
    });
};

/**
 * Creates one or more Metric documents — company-level or master-list — through the v2 API,
 * auto-creating missing metric groups and (company-level, eligible standards only) emission
 * factors along the way. This is the GE-2907 fix on the bulk company-level create path: see the
 * `withScopeReference` call at `PanelMetrics.tsx`'s `createConfiguredMetricRows`.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for the request. On the master-list
 * screen, callers must send `{ master_list: true }` with `group_id`/`company_id` omitted; on the
 * company screen, `{ master_list: false, group_id, company_id }`. `audit_user_email` is set by
 * this function from the current Firebase user, matching every other v2 frontend service; callers
 * should not pass it.
 * @param {Array<CreateMetricClientData>} data The metrics to create.
 * @param {CreateMetricsOptions} options Optional mutation options (audit log, return_data,
 * `include_emission_factors`).
 * @returns {Promise<Array<string> | null | undefined>} The ids of the metrics *actually written*,
 * in write order — not index-matched to `data`, and possibly shorter than `data.length` (the
 * server silently skips a metric missing `metric_group`/`standard`). Only meaningful as a count,
 * not as "the created id for `data[i]`" — see `createMetrics` in the v2 service for the full
 * contract. `null`/`undefined` unless `options.return_data` is set.
 */
export const createMetrics = async (
  metadata: ResolverMutationRequestMetadata,
  data: Array<CreateMetricClientData>,
  options?: CreateMetricsOptions
): Promise<Array<string> | null | undefined> => {
  validateParams(
    { metadata },
    {
      // Optional-tolerant: bare isValidGroupId/isValidCompanyId reject `undefined`, which the
      // master-list screen requires (group_id/company_id omitted entirely).
      'metadata.group_id': {
        validate: (group_id: unknown) => !group_id || isValidGroupId(group_id),
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: (company_id: unknown) => !company_id || isValidCompanyId(company_id),
        message: 'Invalid company_id specified.'
      }
    }
  );

  // `input.data` is `{ metrics: [...] }`, not a bare array — matches
  // `CreateMetricsRequest['input']`'s shape in @esg/syntiro-api-core (unlike UpdateMetricRequest,
  // whose `data` is the client data object directly).
  const input: CreateMetricsRequest['input'] = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    data: { metrics: data },
    options
  };

  return apollo_client_v2
    .mutate({ mutation: CREATE_METRICS, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.createMetrics) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createMetrics'));
      }
      // parseResolverResponse, not `.result` raw: it throws on `metadata.errors` being non-empty,
      // which is how a v2 ERROR envelope (HTTP 200, result: null) surfaces as a failure here
      // instead of silently reporting success.
      return parseResolverResponse<Array<string>>(response.data.createMetrics);
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error ? error.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createMetrics'),
        { metadata, data, options },
        trackingId
      );
    });
};
